import Backbone from 'backbone';
import { logError } from '@ardoq/logging';
import { debounce, defer, throttle } from 'lodash';
import { getAggregatedGraph } from 'graph/aggregatedGraph';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { SettingsBar } from '@ardoq/settings-bar';
import { SettingsType } from '@ardoq/view-settings';
import { getExportsForHtmlView } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { getHierarchicGraph } from 'graph/hierarchicGraph';
import * as encodingUtils from '@ardoq/html';
import { IconName } from '@ardoq/icons';
import { fieldOps } from 'models/utils/fieldOps';
import { fieldOperations } from '@ardoq/core';
import { globalFilters as Filters } from 'collections/filters';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { createElement, Fragment, useEffect, useRef } from 'react';
import { APIFieldAttributes, isSystemFilter, ViewIds } from '@ardoq/api-types';
import {
  buildMinimalScopeDataForFilters,
  parseConditionalFormatting,
} from '@ardoq/view-legend';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';
import { viewHasLegend } from 'views/metaInfoTabs';
import { getSanitizedRgbaClassName } from '@ardoq/color-helpers';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import * as profiling from '@ardoq/profiling';
import { action$, connect, dispatchAction, ofType } from '@ardoq/rxbeach';
import { notifyViewRenderDone } from '../actions';
import { renderMainContentView } from 'streams/views/mainContent/actions';
import { onViewSettingsUpdate } from '../onViewSettingsUpdate';
import { RoadMapViewItem } from './RoadMapViewItem';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import { LegacyRoadmapViewStyle } from './LegacyRoadmapViewStyle';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { ConnectedComponentDescriptionPopover } from './ComponentDescriptionPopover';
import { getRepresentationData, roadmapViewData$ } from './roadmapViewData$';
import { resolveComponentType } from 'streams/resolveComponentTypeFromWorkspaceAndModel';
import type { GraphItemModel } from 'graph/GraphItem';
import type { FieldValue } from 'graph/FieldValue';
import { clearSubscriptions } from 'streams/utils/streamUtils';
import { isContextWorkspaceEmpty$ } from 'streams/views/mainContent/isContextWorkspaceEmpty$';
import { EmptyWorkspace } from 'components/EmptyWorkspace/EmptyWorkspace';
import { classes } from '@ardoq/common-helpers';
import {
  RoadmapViewSettings,
  RoadmapViewType,
  VisualBaseViewProperties,
} from './legacyTypes';
import type { Node } from 'graph/types';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { createRoot } from 'react-dom/client';
import { getViewThumbnailSrc } from 'tabview/consts';
import { slideIsLoading$ } from 'presentation/viewPane/slideIsLoading$';
import { isPresentationMode } from 'env';
import {
  contextWorkspaceOrComponentId$,
  contextReferenceId$,
  workspacesClosed$,
} from 'streams/context/context$';
import { components$ } from 'streams/components/components$';
import { references$ } from 'streams/references/references$';
import {
  notifyFiltersChanged,
  setActivePerspective,
} from 'streams/filters/FilterActions';
import { throttleTime } from 'rxjs/operators';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { WorkspaceModeSlidesEndBanner } from 'appLayout/WorkspaceModeSlidesEndBanner';
import { getGraphInterfaceWithModelInterfaces } from '@ardoq/graph';

const VIEW_ID = ViewIds.ROADMAP;

/**
 * If the viewInstanceId is not specified by subscriber, we get all updates for the view type.
 *
 * We don't make the arg type optional, because this architectural pattern here is deprecated.
 */
const getViewSettingsStream$ = () =>
  getWorkspaceModeViewSettings$(ViewIds.ROADMAP, '');

interface RenderRowArgs {
  tbody: HTMLElement;
  node: Node<GraphItemModel>;
  options: string[];
  field: APIFieldAttributes;
}

const THROTTLE_TIME = 100;

const getThrottledDebouncedRender = (view: RoadmapViewType) =>
  throttle(() => {
    if (view.isActive()) {
      try {
        view.debouncedRender();
      } catch (error) {
        logError(error as Error);
      }
    }
  }, THROTTLE_TIME);

const hasDimensions = (element: Element) => {
  if (!element) {
    return false;
  }
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
};

const regExpFormattingFilter = /filter([^ ]*)/;
const FADE_CLASS_NAME = 'faded';

const fadeOut = (elements: NodeListOf<HTMLElement> | null) => {
  if (!elements?.length) return;

  elements.forEach(element => {
    element.addEventListener(
      'transitionend',
      e => {
        const target = e.currentTarget as HTMLElement;
        target.style.display = 'none';
      },
      {
        once: true,
      }
    );
    element.classList.add(FADE_CLASS_NAME);
  });
};

const fadeIn = (elements: NodeListOf<HTMLElement> | null) => {
  if (!elements?.length) return;

  elements.forEach(element => {
    element.style.display = 'block';
    setTimeout(() => element.classList.remove(FADE_CLASS_NAME), 0);
  });
};

const isFieldValue = (dataModel: GraphItemModel): dataModel is FieldValue =>
  Boolean((dataModel as FieldValue).field);

const getOptionOnClick =
  (viewId: ViewIds.ROADMAP, isActive: boolean, name: string) => () => {
    const selectedFieldName = isActive ? undefined : name;
    handleStoreSetting(viewId, { selectedFieldName });
  };

const getFieldOptions = (
  viewId: ViewIds.ROADMAP,
  viewstate: RoadmapViewSettings,
  workspaceFields: readonly APIFieldAttributes[]
) => {
  return workspaceFields
    .filter(
      field =>
        (fieldOperations.isListField(field) ||
          fieldOperations.isSelectedMultipleListField(field)) &&
        fieldOperations.appliesToComponents(field)
    )
    .map(field => {
      const name = field.name;
      const isActive = viewstate.selectedFieldName === name;

      return {
        label: field.label || name,
        name,
        isActive,
        onClick: getOptionOnClick(viewId, isActive, name),
        type: DropdownOptionType.OPTION,
      };
    });
};

const getLeftMenuConfig = (
  viewId: ViewIds.ROADMAP,
  viewstate: RoadmapViewSettings,
  workspaceFields: readonly APIFieldAttributes[]
) => [
  {
    id: 'fieldDropdown',
    label: t(
      // i18nLocale$.state OK here: Backbone view render() is called by Backbone framework on demand; no React subscription path
      i18nLocale$.state,
      'tabview:roadmap.selectField',
      'Select field'
    ),
    iconName: IconName.FORMAT_LIST_BULLETED,
    options: getFieldOptions(viewId, viewstate, workspaceFields),
    type: SettingsType.DROPDOWN,
  },
];

const RoadmapView = Backbone.ViewDEPRECATED.extend({
  tagName: 'div',
  slideIsLoading: false,
  className: 'view-plugin',
  viewstate: null,
  _setViewstate: function (
    this: RoadmapViewType,
    viewstate: RoadmapViewSettings
  ) {
    this.viewstate = viewstate;
    this.render();
  },
  id: VIEW_ID,
  description: 'Roadmap',
  name: 'Roadmap view',
  exampleImage: getViewThumbnailSrc(VIEW_ID),
  HTMLToPNGExport: true,
  SVGExport: true,
  SVGToPNGExport: true,
  PresentationExport: true,
  canExport: true,
  inactiveLegends: new Map(),
  events: {
    'click .legend .item': 'toggleRoadmapLegend',
  },
  toggleRoadmapLegend: function (this: RoadmapViewType, el: Event) {
    const currentTarget = el.currentTarget as HTMLElement;
    const cssClass = currentTarget.className;
    const cssSelector =
      cssClass && cssClass.indexOf('filter') > -1
        ? cssClass.replace(/.* (filter\w+).*/gi, '$1')
        : cssClass?.replace(/.* (p\d+).*/gi, '$1');

    if (!cssSelector) {
      return;
    }
    const wasInactive = this.inactiveLegends.get(cssSelector) ?? false;
    const isInactive = !wasInactive;
    this.inactiveLegends.set(cssSelector, isInactive);

    const rowContent = this.el.querySelectorAll<HTMLElement>(
      `table .${cssSelector}`
    );
    if (isInactive) {
      currentTarget.style.opacity = '0.4';
      fadeOut(rowContent);
    } else {
      currentTarget.style.opacity = '1';
      fadeIn(rowContent);
    }
  },
  pluginInit: function (
    this: RoadmapViewType,
    props: VisualBaseViewProperties
  ) {
    this.containerElement = props.containerElement;
    this.menuContainerElement = props.menuContainerElement;

    this.exports = getExportsForHtmlView({
      container: this.containerElement,
      exportedViewMetadata: {
        name: VIEW_ID,
        isViewpointMode: false,
      },
      ...getSharedExportFunctions(),
      // i18nLocale$.state OK here: Backbone view init() runs at instantiation time outside React; no subscription path
      labels: getExportLabels(i18nLocale$.state),
    });

    this.streamSubscriptions.push(
      getViewSettingsStream$().subscribe(viewstate =>
        this._setViewstate(viewstate)
      )
    );

    this.streamSubscriptions.push(
      groupingRules$.subscribe(() => this.render())
    );

    this.mountViewReactRoot();
  },
  mountViewReactRoot: function (this: RoadmapViewType) {
    const mountElement = document.createElement('div');
    this.containerElement.append(mountElement);
    this.viewReactRootElement = mountElement;
    this.viewReactRoot = createRoot(mountElement);
    this.viewReactRoot.render(
      <>
        <LegacyRoadmapViewStyle />
        <ConnectedComponentDescriptionPopover
          rootRef={{ current: this.containerElement }}
        />
      </>
    );
  },
  getFieldByName: function (this: RoadmapViewType, name?: string) {
    if (!name) {
      return null;
    }

    return this.data.workspaceFields.find(field => field.name === name) ?? null;
  },
  getNodes: function (this: RoadmapViewType) {
    let graph, nodes;
    const { hasGroupingRules, context } = this.data;
    if (!hasGroupingRules) {
      const { componentId, workspaceId } = context;
      if (componentId) {
        graph = getHierarchicGraph().getGraphByComponent({
          componentId,
          maxDegreesIncoming: 0,
          maxDegreesOutgoing: 0,
          includeParents: true,
        });
      } else {
        graph = getHierarchicGraph().getGraphByWorkspace({
          workspaceId,
          maxDegreesIncoming: 0,
          maxDegreesOutgoing: 0,
        });
      }

      nodes = Array.from(graph.nodes.values());
    } else {
      graph = getAggregatedGraph().getFullGraph();
      /* DEFAULTING TO ENTIRE GRAPH, AS THE GROUP SHOULD OVERRIDE ANY CONTEXT.
        if (Context.component()) {
          graph = getAggregatedGraph().getGraphByComponent({
            component: Context.component(),
            maxDegrees: 0
          });
        } else {
          graph = getAggregatedGraph().getFullGraph();
        }
        */
      nodes = graph.rootNodes.flatten();
    }

    return nodes;
  },
  renderRow: function (
    this: RoadmapViewType,
    { tbody, node, options, field }: RenderRowArgs
  ) {
    const dedupKey = node.isComponent()
      ? node.dataModel._id
      : (node.dataModel as Backbone.Model).cid;
    if (
      this.duplicateNodeCheck![dedupKey] ||
      !node.hasChildren() ||
      !node.isIncludedInContextByFilter()
    ) {
      return;
    }

    this.duplicateNodeCheck![dedupKey] = true;
    const optionNodes: Record<string, Node<GraphItemModel>[]> = {};
    options.forEach(opt => {
      optionNodes[opt] = [];
    });

    let hasChildWithMatchingOption = false;
    node.children.forEach(child => {
      if (!child.isComponent()) {
        return;
      }
      const fieldName = field.name;
      const rawValue = child.dataModel[fieldName];
      const fieldValues = rawValue instanceof Array ? rawValue : [rawValue];

      fieldValues.forEach(fieldValue => {
        if (fieldValue && optionNodes[fieldValue]) {
          hasChildWithMatchingOption = true;
          optionNodes[fieldValue].push(child);
        }
      });
    });

    if (hasChildWithMatchingOption) {
      const tr = document.createElement('tr');
      tbody.append(tr);

      const th = document.createElement('th');
      th.innerText = isFieldValue(node.dataModel)
        ? node.dataModel.value
        : encodingUtils.unescapeHTML(node.name());
      tr.append(th);

      options.forEach(opt => {
        const td = document.createElement('td');
        tr.append(td);
        const cellRoot = createRoot(td);
        this.contentRoots.push(cellRoot);
        cellRoot.render(
          <>
            {optionNodes[opt].map(optionNode => {
              const name = optionNode.name();
              return (
                <RoadMapViewItem
                  key={name}
                  node={optionNode}
                  label={name}
                  isLegendItem={false}
                  representationData={getRepresentationData(
                    this.data,
                    dataModelId(optionNode)
                  )}
                />
              );
            })}
          </>
        );
        optionNodes[opt].forEach(optionNode => {
          const laneSpanClass = optionNode.getCSS({
            useAsBackgroundStyle: true,
          });
          this.addLegendForMap(optionNode, laneSpanClass);
        });
      });
    }

    node.children.forEach(child =>
      this.renderRow({ tbody, node: child, options, field })
    );
  },
  addLegendForMap: function (
    this: RoadmapViewType,
    node: Node<GraphItemModel>,
    cssString: string
  ) {
    const [, filterColor] = regExpFormattingFilter.exec(cssString) || [
      null,
      null,
    ];

    const filterColorNotInLegendMap =
      filterColor && !this.legendMap![filterColor];
    const filter = filterColorNotInLegendMap
      ? Filters.getFormattingFilters()
          .filter(({ attributes }) => !isSystemFilter(attributes))
          .find(currentFilter => {
            const color = currentFilter.get('color');
            return (
              typeof color === 'string' &&
              (color === `#${filterColor}` ||
                getSanitizedRgbaClassName(color) === `filter${filterColor}`)
            );
          })
      : undefined;

    if (filter && filterColorNotInLegendMap) {
      const filterJSON = filter.toJSON();
      const scopeData = buildMinimalScopeDataForFilters(
        getGraphInterfaceWithModelInterfaces(),
        [filterJSON]
      );
      const [first] = parseConditionalFormatting(
        scopeData,
        filterJSON,
        // i18nLocale$.state OK here: imperative legend builder in a class
        // component render path; threading would require routing through
        // the class state.
        i18nLocale$.state,
        undefined
      );
      const filterDescription = first?.filterDescription ?? '';
      this.legendMap![filterColor] = {
        node,
        isLegendItem: true,
        label: filterDescription,
        representationData: getRepresentationData(this.data, dataModelId(node)),
      };
    } else if (node.isComponent()) {
      const component = node.dataModel;
      if (!this.legendMap![component.typeId]) {
        this.legendMap![component.typeId] = {
          node,
          isLegendItem: true,
          label:
            resolveComponentType(component, this.data.workspaceAndModel)
              ?.name ?? '',
          representationData: getRepresentationData(this.data, component._id),
        };
      }
    }
  },
  localRender: function (this: RoadmapViewType) {
    const transaction = profiling.startTransaction(
      'roadmap view render',
      1000,
      profiling.Team.INSIGHT
    );
    this.unmountContentRoots();
    this.el.replaceChildren();
    const { workspaceId } = this.data.context;
    this.duplicateNodeCheck = {};
    this.legendMap = {};
    this.renderSettingsBar();
    if (!workspaceId) {
      dispatchAction(notifyViewRenderDone(VIEW_ID));
      return;
    }

    const field = this.getFieldByName(this.viewstate.selectedFieldName);
    if (!field) {
      dispatchAction(notifyViewRenderDone(VIEW_ID));
      return;
    }

    const nodes = this.getNodes();
    let options: string[] = [];
    if (
      fieldOperations.isListField(field) ||
      fieldOperations.isSelectedMultipleListField(field)
    ) {
      options = fieldOps.getAllowedValues(field);
    } else {
      return;
    }

    const legendContainer = document.createElement('div');
    legendContainer.classList.add('legend');
    this.getContainerElement().append(legendContainer);

    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const tbody = document.createElement('tbody');
    const tr = document.createElement('tr');

    this.getContainerElement().append(table);
    table.append(thead, tbody);
    table.setAttribute('data-context-menu', OBJECT_CONTEXT_MENU_NAME);
    thead.append(tr);
    tr.append(document.createElement('th'));
    options.forEach(option => {
      const th = document.createElement('th');
      th.classList.add('header');
      th.textContent = option;
      tr.append(th);
    });

    nodes.forEach(node => {
      if (!node.isIncludedInContextByFilter()) {
        return;
      }

      this.renderRow({ node, tbody, options, field });
    });
    const legendRoot = createRoot(legendContainer);
    this.contentRoots.push(legendRoot);
    legendRoot.render(
      <>
        {Object.values(this.legendMap).map(props =>
          createElement(RoadMapViewItem, props)
        )}
      </>
    );

    profiling.endTransaction(transaction, {
      viewId: VIEW_ID,
      metadata: {
        nodes: nodes.length,
      },
    });
    dispatchAction(notifyViewRenderDone(VIEW_ID));
  },
  renderSettingsBar: function (this: RoadmapViewType) {
    // i18nLocale$.state OK here: Backbone view render() is called by Backbone framework on demand; no React subscription path
    const i18nLocale = i18nLocale$.state;
    this.settingsBarRoot ??= createRoot(this.menuContainerElement);
    this.settingsBarRoot.render(
      createElement(
        Fragment,
        null,
        createElement(SettingsBar, {
          viewId: this.id,
          i18nLocale,
          leftMenu: getLeftMenuConfig(
            this.id,
            this.viewstate,
            this.data.workspaceFields
          ),
          rightMenu: getRightMenuConfig({
            isViewpointMode: false,
            viewId: this.id,
            viewstate: this.viewstate,
            exports: this.exports,
            withLegend: viewHasLegend(this.id),
            knowledgeBaseLink: KnowledgeBaseLink.ROADMAP,
            onViewSettingsUpdate,
            i18nLocale,
          }),
        }),
        createElement(WorkspaceModeSlidesEndBanner)
      )
    );
  },

  cleanUp: function () {
    // removes all event listeners from element(s)
    // https://stackoverflow.com/questions/19469881/remove-all-event-listeners-of-specific-type
    this.el
      .querySelectorAll('div.group, div.ref')
      .forEach((element: HTMLElement) => {
        element.replaceWith(element.cloneNode(true));
      });
  },

  unmountContentRoots: function (this: RoadmapViewType) {
    this.contentRoots.forEach(root => root.unmount());
    this.contentRoots = [];
  },

  remove: function (this: RoadmapViewType) {
    this.isRemoved = true;
    this.debouncedRender.cancel();
    window.removeEventListener('resize', this.boundOnResize);
    this.streamSubscriptions = clearSubscriptions(this.streamSubscriptions);
    this.viewReactRoot?.unmount();
    this.viewReactRootElement?.remove();
    this.viewReactRoot = null;
    this.viewReactRootElement = null;
    this.settingsBarRoot?.unmount();
    this.settingsBarRoot = null;
    this.unmountContentRoots();
    return Backbone.ViewDEPRECATED.prototype.remove.call(this);
  },
  getContainerElement: function (this: RoadmapViewType) {
    if (!this.pluginContainerElement) {
      this.el.id = `_plugin${this.id}`;
      this.el.classList.add('roadmap-view');
      this.containerElement.append(this.el);
      this.pluginContainerElement = this.el;
    }
    return this.pluginContainerElement;
  },
  localInit: function (this: RoadmapViewType, props: VisualBaseViewProperties) {
    this.streamSubscriptions.push(
      roadmapViewData$.subscribe(data => (this.data = data))
    );
    this.streamSubscriptions.push(
      workspacesClosed$.subscribe(() => {
        this.getContainerElement()
          .querySelector('ul.incoming')
          ?.replaceChildren();
        this.cleanUp();
      })
    );
    const tdr = getThrottledDebouncedRender(this);
    this.streamSubscriptions.push(components$.subscribe(tdr));
    this.streamSubscriptions.push(references$.subscribe(tdr));
    this.streamSubscriptions.push(
      contextWorkspaceOrComponentId$.subscribe(tdr)
    );
    this.streamSubscriptions.push(contextReferenceId$.subscribe(tdr));
    this.streamSubscriptions.push(
      action$
        .pipe(
          ofType(
            notifyFiltersChanged,
            setActivePerspective,
            renderMainContentView
          ),
          throttleTime(THROTTLE_TIME)
        )
        .subscribe(() => this.debouncedRender())
    );

    this.pluginInit(props);
  },
  render: function (
    this: RoadmapViewType,
    model: unknown,
    event: unknown,
    callback: unknown,
    options: unknown
  ) {
    if (!this.data.context.workspaceId) {
      return;
    }

    if (this.isActive() && this.isVisible()) {
      this._rendering = true;
      try {
        defer(() => {
          if (this.isRemoved) {
            return;
          }
          this.localRender(model, event, callback, options);
          this._rendering = false;
        });
      } catch (error) {
        logError(error as Error, 'Error while redrawing view plugin', {
          viewPluginName: this.name,
        });
      }
    }
  },

  isActive: function () {
    const containerElement = this.containerElement;
    return (
      !this.slideIsLoading &&
      document.documentElement.contains(containerElement) &&
      hasDimensions(containerElement)
    );
  },
  isVisible: function (this: RoadmapViewType) {
    const containerElementParent = this.containerElement.parentElement;
    return Boolean(
      containerElementParent &&
      (containerElementParent.offsetWidth ||
        containerElementParent.offsetHeight ||
        containerElementParent.getClientRects().length)
    );
  },
  initialize: function (
    this: RoadmapViewType,
    props: VisualBaseViewProperties
  ) {
    this.streamSubscriptions = [];
    this.contentRoots = [];
    this.containerElement = props.containerElement;
    this.menuContainerElement = props.menuContainerElement;
    this.debouncedRender = debounce(this.render, 150);

    this.localInit(props);

    this.boundOnResize = this.onResize.bind(this);
    window.addEventListener('resize', this.boundOnResize);

    if (isPresentationMode()) {
      this.streamSubscriptions.push(
        slideIsLoading$.subscribe(slideIsLoading => {
          this.slideIsLoading = slideIsLoading;
        })
      );
    }
  },
  onResize: function () {
    if (this.isActive()) {
      this.debouncedRender();
    }
  },
  deselected: function () {
    if (this.stop) {
      this.stop();
    }

    this.containerElement.querySelector('ul.incoming')?.replaceChildren();

    if (!this._rendering) {
      this.cleanUp();
    }
  },
});

const create = (props: VisualBaseViewProperties): RoadmapViewType =>
  new RoadmapView(props);

const TAB_PANE_CLASS_NAMES = classes(
  'tab-pane',
  `tab${VIEW_ID}`,
  `${VIEW_ID}Tab`,
  'active'
);

const createClassedDiv = (className: string): HTMLDivElement => {
  const element = document.createElement('div');
  element.classList.add(className);
  return element;
};

type LegacyRoadmapHostProps = {
  isContextWorkspaceEmpty: boolean;
};

type HostElements = {
  containerElement: HTMLDivElement;
  menuContainerElement: HTMLDivElement;
};

const LegacyRoadmapHost = ({
  isContextWorkspaceEmpty,
}: LegacyRoadmapHostProps) => {
  const mountPoint = useRef<HTMLDivElement>(null);
  const elements = useRef<HostElements | null>(null);
  const view = useRef<RoadmapViewType | null>(null);

  useEffect(() => {
    const hostElements: HostElements = {
      containerElement: createClassedDiv('contentPane'),
      menuContainerElement: createClassedDiv('menuContainer'),
    };
    elements.current = hostElements;
    view.current = create(hostElements);

    return () => {
      const roadmapView = view.current;
      view.current = null;
      elements.current = null;
      roadmapView?.deselected();
      // remove() unmounts React roots, which trips React 19's "unmount during
      // render" race when run inside the commit phase.
      queueMicrotask(() => roadmapView?.remove());
    };
  }, []);

  useEffect(() => {
    if (isContextWorkspaceEmpty || !mountPoint.current || !elements.current) {
      return;
    }
    const { containerElement, menuContainerElement } = elements.current;
    mountPoint.current.append(menuContainerElement, containerElement);
    view.current?.debouncedRender();
  }, [isContextWorkspaceEmpty]);

  if (isContextWorkspaceEmpty) {
    return <EmptyWorkspace />;
  }
  return <div ref={mountPoint} className={TAB_PANE_CLASS_NAMES} />;
};

export const ConnectedLegacyRoadmapView = connect(
  LegacyRoadmapHost,
  isContextWorkspaceEmpty$
);
